import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, Mail } from "lucide-react";
import { IDS } from "@/lib/testIds";
import { useLang } from "@/lib/LangContext";
import { BUSINESS } from "@/lib/data";

export default function Nav() {
  const { ui, lang, toggle } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();

  const links = [
    { to: "/", label: ui.nav.home, id: IDS.nav.home },
    { to: "/services", label: ui.nav.services, id: IDS.nav.services },
    { to: "/cities", label: ui.nav.cities, id: IDS.nav.cities },
    { to: "/insights", label: ui.nav.insights, id: "nav-insights" },
    { to: "/about", label: ui.nav.about, id: IDS.nav.about },
    { to: "/contact", label: ui.nav.contact, id: IDS.nav.contact },
  ];

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 20);
    on();
    window.addEventListener("scroll", on);
    return () => window.removeEventListener("scroll", on);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [loc.pathname]);

  return (
    <header
      data-testid={IDS.nav.root}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-[hsl(0_0%_5%)] border-b border-border" : "bg-transparent"
      }`}
    >
      {/* Top utility strip — phone + email */}
      <div className="hidden md:block border-b border-border/60 bg-[hsl(0_0%_4%)]/80">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12 flex items-center justify-end gap-6 h-9 font-mono text-[10px] tracking-[0.25em] uppercase text-muted-foreground">
          <a
            href={BUSINESS.phoneHref}
            data-testid="nav-phone"
            className="flex items-center gap-2 hover:text-primary transition-colors"
          >
            <Phone size={12} strokeWidth={1.5} className="text-primary" />
            {BUSINESS.phone}
          </a>
          <span className="opacity-30">·</span>
          <a
            href={BUSINESS.emailHref}
            data-testid="nav-email"
            className="flex items-center gap-2 hover:text-primary transition-colors"
          >
            <Mail size={12} strokeWidth={1.5} className="text-primary" />
            {BUSINESS.email}
          </a>
        </div>
      </div>

      {/* Main nav row */}
      <div className="mx-auto max-w-[1600px] px-6 md:px-12 flex items-center justify-between h-16 md:h-20">
        <Link
          to="/"
          data-testid={IDS.nav.logo}
          className="flex items-center gap-3 group"
        >
          {/* Maze Security Solutions */}
          <div className="w-10 h-10 border border-foreground grid place-items-center group-hover:bg-primary group-hover:border-primary group-hover:text-white transition-colors">
            {/* MS */}
            <span className="font-serif text-base leading-none tracking-tight">MS</span>
          </div>
          <div className="leading-none">
            <div className="font-mono text-[10px] tracking-[0.25em] text-muted-foreground uppercase">
              {ui.nav.established}
            </div>
            <div className="font-serif text-lg md:text-xl tracking-tight">
              Maze Security<span className="italic text-primary">.</span>
            </div>
            <div className="font-mono text-[9px] tracking-[0.2em] text-muted-foreground uppercase mt-0.5">
              New Brunswick Security Service
            </div>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={l.id}
              className={({ isActive }) =>
                `font-mono text-xs tracking-[0.2em] uppercase transition-colors ${
                  isActive
                    ? "text-primary"
                    : "text-foreground/80 hover:text-primary"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <LangToggle lang={lang} toggle={toggle} labels={ui.lang} />
          <Link
            to="/contact#quote"
            data-testid={IDS.nav.cta}
            className="inline-flex items-center gap-2 border border-foreground px-5 py-3 font-mono text-xs tracking-[0.2em] uppercase hover:bg-primary hover:border-primary hover:text-white transition-colors"
          >
            {ui.nav.cta}
          </Link>
        </div>

        {/* Mobile: quick call button + lang toggle + hamburger */}
        <div className="lg:hidden flex items-center gap-2">
          <a
            href={BUSINESS.phoneHref}
            data-testid="nav-phone-mobile"
            aria-label={BUSINESS.phone}
            className="w-10 h-10 border border-border grid place-items-center text-primary hover:bg-primary hover:text-white hover:border-primary transition-colors"
          >
            <Phone size={16} strokeWidth={1.5} />
          </a>
          <LangToggle lang={lang} toggle={toggle} labels={ui.lang} compact />
          <button
            data-testid={IDS.nav.mobileToggle}
            className="w-10 h-10 border border-border grid place-items-center"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid={IDS.nav.mobileMenu}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.76, 0, 0.24, 1] }}
            className="lg:hidden bg-[hsl(0_0%_5%)] border-t border-border overflow-hidden"
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  data-testid={`${l.id}-mobile`}
                  className={({ isActive }) =>
                    `font-serif text-3xl tracking-tight ${
                      isActive ? "text-primary" : "text-foreground"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/contact#quote"
                data-testid={`${IDS.nav.cta}-mobile`}
                className="mt-4 inline-flex items-center justify-center border border-foreground px-6 py-4 font-mono text-xs tracking-[0.2em] uppercase hover:bg-primary hover:border-primary transition-colors"
              >
                {ui.nav.cta}
              </Link>
              <div className="pt-6 border-t border-border space-y-3">
                <a
                  href={BUSINESS.phoneHref}
                  data-testid="mobile-menu-phone"
                  className="flex items-center gap-3 font-mono text-sm tracking-widest text-foreground/85 hover:text-primary transition-colors"
                >
                  <Phone size={14} strokeWidth={1.5} className="text-primary" />
                  {BUSINESS.phone}
                </a>
                <a
                  href={BUSINESS.emailHref}
                  data-testid="mobile-menu-email"
                  className="flex items-center gap-3 font-mono text-sm tracking-widest text-foreground/85 hover:text-primary transition-colors"
                >
                  <Mail size={14} strokeWidth={1.5} className="text-primary" />
                  {BUSINESS.email}
                </a>
                <div className="font-mono text-xs text-muted-foreground pt-2">
                  {lang === "fr" ? "24 / 7 · Bilingue · Licencié" : "24 / 7 · Bilingual · Licensed"}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

function LangToggle({ lang, toggle, labels, compact }) {
  return (
    <button
      onClick={toggle}
      data-testid="lang-toggle"
      aria-label={labels.toggleAria}
      className={`relative border border-border font-mono text-[10px] tracking-[0.25em] uppercase flex items-center ${
        compact ? "h-10" : "h-10"
      } hover:border-primary transition-colors`}
    >
      <span
        className={`px-3 h-full flex items-center transition-colors ${
          lang === "en" ? "bg-primary text-white" : "text-muted-foreground"
        }`}
      >
        {labels.en}
      </span>
      <span
        className={`px-3 h-full flex items-center border-l border-border transition-colors ${
          lang === "fr" ? "bg-primary text-white" : "text-muted-foreground"
        }`}
      >
        {labels.fr}
      </span>
    </button>
  );
}
